import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils-format";
import { Plus, MagnifyingGlass, Tag, X as XIcon } from "@phosphor-icons/react";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [pricingFor, setPricingFor] = useState(null);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", address: "", country: "", notes: "" });

  const load = () => api.get("/customers", { params: search ? { search } : {} }).then((r) => setCustomers(r.data));
  useEffect(() => { load(); }, []);
  const onSearch = (e) => { e.preventDefault(); load(); };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/customers", form);
      toast.success("Customer created");
      setOpen(false);
      setForm({ name: "", company: "", email: "", phone: "", address: "", country: "", notes: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-8" data-testid="customers-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Directory</p>
          <h1 className="text-4xl font-black tracking-tighter">Customers</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="new-customer-button">
              <Plus size={16} weight="bold" className="mr-1" /> New Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader><DialogTitle>New Customer</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3" data-testid="new-customer-form">
              {["name", "company", "email", "phone", "address", "country", "notes"].map((k) => (
                <div key={k}>
                  <Label className="text-[11px] uppercase tracking-widest text-zinc-500">{k}</Label>
                  <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required={k === "name"} className="border-zinc-300 rounded-md mt-1" data-testid={`customer-${k}-input`} />
                </div>
              ))}
              <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="save-customer-button">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <form onSubmit={onSearch} className="relative mb-4">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, phone, company…"
          className="pl-9 border-zinc-300 rounded-md" data-testid="customer-search-input" />
      </form>

      <div className="border border-zinc-200 bg-white" data-testid="customers-table">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2.5">Name</th>
              <th className="text-left px-4 py-2.5">Company</th>
              <th className="text-left px-4 py-2.5">Email</th>
              <th className="text-left px-4 py-2.5">Phone</th>
              <th className="text-left px-4 py-2.5">Country</th>
              <th className="text-left px-4 py-2.5">Pricing</th>
              <th className="text-right px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.customer_id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`customer-row-${c.customer_id}`}>
                <td className="px-4 py-3 text-sm font-medium">{c.name}</td>
                <td className="px-4 py-3 text-sm">{c.company || "—"}</td>
                <td className="px-4 py-3 text-sm">{c.email || "—"}</td>
                <td className="px-4 py-3 text-sm">{c.phone || "—"}</td>
                <td className="px-4 py-3 text-sm">{c.country || "—"}</td>
                <td className="px-4 py-3 text-xs">
                  {c.pricing_overrides?.length > 0
                    ? <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded font-semibold uppercase tracking-widest">{c.pricing_overrides.length} custom</span>
                    : <span className="text-zinc-400">Standard</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setPricingFor(c)} className="text-[10px] uppercase tracking-widest text-zinc-600 hover:text-zinc-900 font-semibold flex items-center gap-1 ml-auto" data-testid={`pricing-btn-${c.customer_id}`}>
                    <Tag size={11} weight="bold" /> Pricing
                  </button>
                </td>
              </tr>
            ))}
            {customers.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-sm text-zinc-500">No customers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {pricingFor && <CustomerPricingDialog customer={pricingFor} onClose={() => { setPricingFor(null); load(); }} />}
    </div>
  );
}

function CustomerPricingDialog({ customer, onClose }) {
  const [products, setProducts] = useState([]);
  const [overrides, setOverrides] = useState(customer.pricing_overrides || []);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/products", { params: { active_only: true } }).then((r) => setProducts(r.data));
  }, []);

  const productById = (id) => products.find((p) => p.product_id === id);
  const overrideFor = (id) => overrides.find((o) => o.product_id === id);

  const enableForProduct = (product) => {
    if (overrideFor(product.product_id)) return;
    setOverrides([...overrides, {
      product_id: product.product_id,
      price_tiers: (product.price_tiers?.length ? product.price_tiers : [{ min_qty: 1, unit_price: product.total_price }]).map((t) => ({ ...t })),
    }]);
  };

  const removeOverride = (product_id) => {
    setOverrides(overrides.filter((o) => o.product_id !== product_id));
  };

  const updateTier = (product_id, i, key, value) => {
    setOverrides(overrides.map((o) => {
      if (o.product_id !== product_id) return o;
      const tiers = o.price_tiers.map((t, j) => j === i ? { ...t, [key]: value } : t);
      return { ...o, price_tiers: tiers };
    }));
  };

  const addTier = (product_id) => {
    setOverrides(overrides.map((o) => o.product_id === product_id
      ? { ...o, price_tiers: [...o.price_tiers, { min_qty: 1, unit_price: 0 }] }
      : o
    ));
  };

  const removeTier = (product_id, i) => {
    setOverrides(overrides.map((o) => o.product_id === product_id
      ? { ...o, price_tiers: o.price_tiers.filter((_, j) => j !== i) }
      : o
    ));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/customers/${customer.customer_id}/pricing`, { overrides });
      toast.success("Custom pricing saved");
      onClose();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="customer-pricing-dialog">
        <DialogHeader><DialogTitle>Custom Pricing · {customer.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-zinc-600">Override the standard product tiers for this customer only. If not set, the customer gets the product's default tiers.</p>

          {overrides.map((o) => {
            const p = productById(o.product_id);
            if (!p) return null;
            return (
              <div key={o.product_id} className="border border-blue-200 bg-blue-50/30 p-4" data-testid={`override-${o.product_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold">{p.name} · {p.unit_size}</p>
                    <p className="text-[10px] text-zinc-500 font-mono">Standard: {p.price_tiers?.map((t) => `${t.min_qty}+ ${formatCurrency(t.unit_price, p.currency)}`).join(" · ") || formatCurrency(p.total_price, p.currency)}</p>
                  </div>
                  <button onClick={() => removeOverride(o.product_id)} className="text-[10px] uppercase tracking-widest text-red-600 hover:text-red-800 font-bold" data-testid={`remove-override-${o.product_id}`}>Remove Override</button>
                </div>
                <div className="space-y-2">
                  {o.price_tiers.map((t, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs text-zinc-600 whitespace-nowrap">Qty ≥</span>
                      <Input type="number" min="1" value={t.min_qty}
                        onChange={(e) => updateTier(o.product_id, i, "min_qty", parseInt(e.target.value, 10) || 1)}
                        className="border-zinc-300 rounded-md h-8 w-24" data-testid={`custom-tier-min-${o.product_id}-${i}`} />
                      <span className="text-xs text-zinc-600">→ unit price</span>
                      <Input type="number" step="0.01" value={t.unit_price}
                        onChange={(e) => updateTier(o.product_id, i, "unit_price", parseFloat(e.target.value) || 0)}
                        className="border-zinc-300 rounded-md h-8 flex-1" data-testid={`custom-tier-price-${o.product_id}-${i}`} />
                      <span className="text-xs text-zinc-500 whitespace-nowrap">{p.currency}</span>
                      <button onClick={() => removeTier(o.product_id, i)} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 rounded">
                        <XIcon size={12} weight="bold" />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => addTier(o.product_id)} className="text-[10px] uppercase tracking-widest text-blue-700 hover:text-blue-900 font-bold flex items-center gap-1 mt-1">
                    <Plus size={11} weight="bold" /> Add tier
                  </button>
                </div>
              </div>
            );
          })}

          <div className="border border-zinc-200 p-4">
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Add override for another product</p>
            <div className="flex flex-wrap gap-2">
              {products.filter((p) => !overrideFor(p.product_id)).map((p) => (
                <button key={p.product_id} onClick={() => enableForProduct(p)} className="px-3 py-1.5 border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white text-xs font-semibold transition-colors" data-testid={`enable-override-${p.product_id}`}>
                  <Plus size={11} weight="bold" className="inline mr-1" /> {p.name}
                </button>
              ))}
              {products.filter((p) => !overrideFor(p.product_id)).length === 0 && (
                <p className="text-xs text-zinc-500">All active products have overrides.</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <Button variant="outline" onClick={onClose} className="border-zinc-300 rounded-md">Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="save-pricing-button">
              {busy ? "Saving…" : "Save Pricing"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
